import { Controller } from "@hotwired/stimulus"

const SUBMIT_DEBOUNCE_MS = 250
// Rows shown in a venue drill-down before the search box has to earn the rest.
// Mirrors FeedHelper::VENUE_DEFAULT_VISIBLE, which renders the same slice.
const VENUE_DEFAULT_VISIBLE = 15

export default class extends Controller {
  static targets = ["panel", "trigger"]

  toggle(event) {
    event.stopPropagation()
    const panel = event.currentTarget.nextElementSibling
    const isOpen = !panel.classList.contains("hidden")
    this.closeAll()
    if (!isOpen) panel.classList.remove("hidden")
  }

  // A multi-select checkbox changed: update its trigger label optimistically
  // (0 → rest label, 1 → the option name, 2+ → the plural word + count pill),
  // then debounce-submit so rapid toggles coalesce into one request. The
  // server response is authoritative for counts and results.
  update(event) {
    this.#refresh(event.currentTarget.closest("[data-filter-dropdown]"))
    this.#debouncedSubmit()
  }

  clear(event) {
    event.stopPropagation()
    const dropdown = event.currentTarget.closest("[data-filter-dropdown]")
    dropdown.querySelectorAll("input[type=checkbox]").forEach(cb => (cb.checked = false))
    this.#applyVenuePrecedence(dropdown)
    this.#refresh(dropdown)
    this.#debouncedSubmit()
  }

  // A source that owns venues swaps the dropdown's root level for its own
  // second level in place, rather than opening a nested popover.
  drillIn(event) {
    event.stopPropagation()
    const dropdown = event.currentTarget.closest("[data-filter-dropdown]")
    dropdown.querySelector("[data-venue-root]").hidden = true
    const panel = dropdown.querySelector("[data-venue-panel]")
    panel.hidden = false
    const search = panel.querySelector("[data-venue-search]")
    requestAnimationFrame(() => search.focus())
  }

  drillOut(event) {
    event.stopPropagation()
    const dropdown = event.currentTarget.closest("[data-filter-dropdown]")
    dropdown.querySelector("[data-venue-panel]").hidden = true
    dropdown.querySelector("[data-venue-root]").hidden = false
  }

  keepOpen(event) {
    event.stopPropagation()
  }

  searchVenues(event) {
    this.#drawVenues(event.currentTarget.closest("[data-venue-panel]"))
  }

  clearVenueSearch(event) {
    event.stopPropagation()
    const panel = event.currentTarget.closest("[data-venue-panel]")
    const search = panel.querySelector("[data-venue-search]")
    search.value = ""
    this.#drawVenues(panel)
    search.focus()
  }

  // Escape clears a non-empty search before it closes the popover, so an
  // accidental press doesn't discard the whole drill-down.
  venueSearchKeydown(event) {
    if (event.key !== "Escape") return
    if (event.currentTarget.value === "") return
    event.stopPropagation()
    const panel = event.currentTarget.closest("[data-venue-panel]")
    event.currentTarget.value = ""
    this.#drawVenues(panel)
  }

  // Picking the whole source subsumes every venue under it, so it drops the
  // individual picks instead of holding both and reading as if they combined.
  // The Level-1 checkbox is the one that submits; the Level-2 "All of …" row
  // is a mirror of it, so either entry point lands in the same state.
  pickSource(event) {
    const dropdown = event.currentTarget.closest("[data-filter-dropdown]")
    dropdown.querySelector("[data-venue-all]").checked = event.currentTarget.checked
    this.#applyVenuePrecedence(dropdown)
    this.update(event)
  }

  pickAllVenues(event) {
    const dropdown = event.currentTarget.closest("[data-filter-dropdown]")
    dropdown.querySelector("[data-venue-source]").checked = event.currentTarget.checked
    this.#applyVenuePrecedence(dropdown)
    this.update(event)
  }

  pickVenue(event) {
    const dropdown = event.currentTarget.closest("[data-filter-dropdown]")
    dropdown.querySelector("[data-venue-source]").checked = false
    dropdown.querySelector("[data-venue-all]").checked = false
    this.#applyVenuePrecedence(dropdown)
    this.update(event)
  }

  submit() {
    this.element.requestSubmit()
  }

  closeAll() {
    this.panelTargets.forEach(p => p.classList.add("hidden"))
  }

  handleEscape(event) {
    if (event.key === "Escape") this.closeAll()
  }

  #applyVenuePrecedence(dropdown) {
    const source = dropdown.querySelector("[data-venue-source]")
    if (!source) return
    const all = source.checked
    dropdown.querySelector("[data-venue-all]").checked = all
    dropdown.querySelectorAll("[data-venue-checkbox]").forEach(cb => {
      if (all) cb.checked = false
      cb.disabled = all
      cb.closest("[data-venue-row]").classList.toggle("opacity-45", all)
    })
    const note = dropdown.querySelector("[data-venue-note]")
    if (note) note.hidden = !all
  }

  // Plain case-insensitive substring over every rendered row, so search reaches
  // past the default slice. An empty query falls back to that slice.
  #drawVenues(panel) {
    const search = panel.querySelector("[data-venue-search]")
    const query = search.value.trim().toLowerCase().replace(/^r\//, "")
    const rows = [...panel.querySelectorAll("[data-venue-row]")]
    let shown = 0
    rows.forEach((row, i) => {
      const visible = query ? row.dataset.venueName.includes(query) : i < VENUE_DEFAULT_VISIBLE
      row.hidden = !visible
      if (visible) shown++
    })

    panel.querySelector("[data-venue-clear]").hidden = query === ""
    const zero = panel.querySelector("[data-venue-zero]")
    const tally = panel.querySelector("[data-venue-tally]")
    if (!zero || !tally) return

    zero.querySelector("[data-venue-zero-query]").textContent = search.value.trim()
    zero.hidden = shown > 0
    tally.hidden = shown === 0
    tally.textContent = shown >= rows.length
      ? `${rows.length} ${rows.length === 1 ? "subreddit" : "subreddits"} with mentions`
      : `Showing ${shown} of ${rows.length}`
  }

  #debouncedSubmit() {
    clearTimeout(this.submitTimer)
    this.submitTimer = setTimeout(() => this.submit(), SUBMIT_DEBOUNCE_MS)
  }

  #refresh(dropdown) {
    if (!dropdown) return
    const trigger = dropdown.querySelector("[data-filter-trigger]")
    const label = trigger.querySelector("[data-filter-label]")
    const pill = trigger.querySelector("[data-filter-count]")
    // Only inputs that actually submit count: the venue panel's "All of …" row
    // is an unnamed mirror of the source checkbox, not a second selection.
    const names = [...dropdown.querySelectorAll("input[type=checkbox][name]:checked")]
      .map(cb => cb.closest("[data-name]")?.dataset.name)
      .filter(Boolean)

    if (names.length === 0) {
      label.textContent = trigger.dataset.rest
      pill.hidden = true
    } else if (names.length === 1) {
      label.textContent = names[0]
      pill.hidden = true
    } else {
      label.textContent = trigger.dataset.word
      pill.textContent = names.length
      pill.hidden = false
    }
    this.#setActive(trigger, names.length > 0)
  }

  // Toggle the active/rest class sets the ERB emitted onto the trigger, so the
  // styling lives only in the template (FeedHelper::FILTER_TRIGGER_*).
  #setActive(trigger, active) {
    trigger.dataset.activeClass.split(" ").forEach(c => trigger.classList.toggle(c, active))
    trigger.dataset.restClass.split(" ").forEach(c => trigger.classList.toggle(c, !active))
    const icon = trigger.querySelector("[data-filter-icon]")
    icon?.classList.toggle("text-brand", active)
    icon?.classList.toggle("text-ink-slate", !active)
  }
}
